<template>
  <div>
    <v-row class="my-3" align="center">
      <v-col>
        <v-btn
          variant="outlined"
          size="small"
          color="primary"
          @click="$emit('back')"
        >
          <v-icon color="primary" start>mdi-arrow-left</v-icon>
          Zurück
        </v-btn>
      </v-col>
      <v-col cols="12" sm="auto" v-if="selected?.length">
        <v-row
          no-gutters
          align="center"
          :justify="mdAndUp ? 'end' : 'start'"
          class="gap-3"
          style="gap: 16px"
        >
          <v-col cols="12" sm="auto">
            <v-btn
              color="primary"
              block
              :size="smAndDown ? 'small' : undefined"
              :disabled="disableImportButton"
              @click="dialog = true"
            >
              <v-icon start size="small">mdi-upload</v-icon>
              Ausgewählte Elemente Importieren
            </v-btn>
          </v-col>

          <v-col cols="12" sm="auto">
            <v-btn
              color="secondary"
              block
              :size="smAndDown ? 'small' : undefined"
              style="color: black"
              @click="deleteSelected"
            >
              <v-icon start size="small">mdi-delete</v-icon>
              Ausgewählte Elemente Löschen
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-data-table
      v-model="selected"
      show-select
      disable-sort
      :headers="type === 'bookables' ? bookableTableHeaders : eventTableHeaders"
      :items="tableRows"
      item-value="id"
      return-object
      density="compact"
      class="elevation-1"
      fixed-header
    >
      <template
        v-for="header in (type === 'bookables'
          ? bookableTableHeaders
          : eventTableHeaders
        ).filter((h) => h.value !== 'actions')"
        v-slot:[`item.${header.value}`]="{ item }"
      >
        <template v-if="header.value === 'image'">
          <div class="pa-3">
            <v-img
              v-if="item.imageUrl"
              :src="item.imageUrl"
              style="width: 72px; height: 72px"
              class="rounded"
              cover
            />
          </div>
        </template>
        <template v-else>
          {{ item[header.value] }}
        </template>
        <div
          v-if="item.errors?.[header.value]"
          class="text-red text-caption mt-1"
        >
          {{ item.errors[header.value] }}
        </div>
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="d-flex">
          <v-btn icon variant="text" @click="deleteRow(item)">
            <v-icon size="18">mdi-delete</v-icon>
          </v-btn>
          <v-btn
            icon
            variant="text"
            :disabled="!!item.errors && Object.keys(item.errors).length > 0"
            @click="importSingleItem(item)"
          >
            <template v-if="uploadingItemId === item.id">
              <v-progress-circular
                indeterminate
                size="18"
                width="2"
              ></v-progress-circular>
            </template>
            <template v-else>
              <v-icon size="18">mdi-upload</v-icon>
            </template>
          </v-btn>
        </div>
      </template>
    </v-data-table>
    <v-dialog v-model="dialog" max-width="500">
      <v-card>
        <v-card-title class="pl-6 pt-4">Import bestätigen</v-card-title>
        <v-card-text class="pt-0">
          <span style="font-size: 16px">
            Sind Sie sicher, dass Sie die ausgewählten
            {{ type === "bookables" ? "Buchungsobjekte" : "Veranstaltungen" }}
            importieren möchten?
          </span>
          <p style="font-size: 12px">
            Dieser Vorgang kann einige Zeit dauern. Bitte laden Sie die Seite
            währenddessen nicht neu.
          </p>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="dialog = false">Abbrechen</v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            class="mr-2"
            :loading="isLoading"
            @click="importSelectedItems(selected)"
            >Import bestätigen</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ApiFileService from "@/services/api/ApiFileService";
import ApiBookablesService from "@/services/api/ApiBookablesService";
import ApiEventService from "@/services/api/ApiEventService";
import ToastService from "@/services/ToastService";
import { parse, isValid } from "date-fns";
import Matomo from "@/utils/Matomo";
import { useDisplay } from "vuetify";

export default {
  name: "ImportPreview",
  props: {
    type: String,
    rows: Array,
    zipImages: Array,
  },
  data() {
    return {
      tableRows: [],
      selected: [],
      dialog: false,
      isLoading: false,
      uploadingItemId: null,
      bookableTableHeaders: [
        { title: "Typ", value: "type" },
        {
          title: "VeranstaltungId",
          value: "eventId",
        },
        { title: "Bezeichnung", value: "title" },
        { title: "Beschreibung", value: "description" },
        { title: "Cover-Bild", value: "image", width: "100px" },
        { title: "Ort", value: "location" },
        { title: "Tags", value: "tags" },
        { title: "Flags", value: "flags" },
        { title: "Preisart", value: "priceType", width: "140px" },
        { title: "Preis(netto)", value: "price", width: "110px" },
        { title: "MwSt", value: "priceValueAddedTax", width: "80px" },
        { title: "Verfügbare Anzahl", value: "amount" },
        { title: "Wiederkehrende Buchungen", value: "allowRecurringBooking" },
        { title: "Automatische Freigabe", value: "autoCommitBooking" },
        { title: "Ist buchbar", value: "isBookable", width: "100px" },
        { title: "Ist sichtbar", value: "isPublic", width: "100px" },
        { title: "Aktionen", value: "actions", width: "120px" },
      ],
      eventTableHeaders: [
        { title: "Name", value: "name" },
        { title: "Kurzbeschreibung", value: "teaserText" },
        { title: "Beschreibung", value: "description" },
        { title: "Titelbild", value: "image" },
        { title: "Tags", value: "tags" },
        { title: "Flags", value: "flags" },
        { title: "Startdatum", value: "startDate", width: "110px" },
        { title: "Startzeit", value: "startTime" },
        { title: "Enddatum", value: "endDate", width: "110px" },
        { title: "Endzeit", value: "endTime" },
        { title: "Veranstaltungsort", value: "locationName" },
        { title: "Straße", value: "street" },
        { title: "Nr.", value: "houseNumber" },
        { title: "PLZ", value: "zip" },
        { title: "Stadt", value: "city" },
        { title: "Veranstalter", value: "organizerName" },
        { title: "Kontaktperson", value: "contactName" },
        { title: "Telefon", value: "contactPhone" },
        { title: "E-Mail", value: "contactEmail" },
        { title: "Registrierung erforderlich", value: "needsRegistration" },
        { title: "Max. Teilnehmer", value: "maxAttendees" },
        { title: "Sichtbar", value: "isPublic" },
        { title: "Aktionen", value: "actions", width: "120px" },
      ],
      bookableTypeMap: {
        ticket: "ticket",
        räume: "room",
        ressourcen: "resource",
        // veranstaltungsort: "event-location",
      },
      priceTypeMap: {
        "pro stück": "per-item",
        "pro stueck": "per-item",
        "pro stunde": "per-hour",
        "pro tag": "per-day",
        "pro quadratmeter": "per-square-meter",
      },
      mdAndUp: false,
      smAndDown: false,
    };
  },
  computed: {
    ...mapGetters({
      tenantId: "tenants/currentTenantId",
    }),
    disableImportButton() {
      return (
        !this.selected.length ||
        this.selected.some(
          (item) => item.errors && Object.keys(item.errors).length
        )
      );
    },
  },
  watch: {
    rows: {
      immediate: true,
      handler(newRows) {
        if (!newRows) return;

        if (this.type === "bookables") {
          this.tableRows = newRows.map((row, idx) => {
            const get = (label) => this.getRowValue(row, label);

            const imageName = get("Bild") || "";
            const priceValue = parseFloat(get("PreisEUR")) || 0;
            const item = {
              id: idx + 1,
              type: get("Typ") || "",
              eventId: get("VeranstaltungId") || "",
              title: get("Bezeichnung") || "",
              description: get("Beschreibung") || "",
              location: get("Ort") || "",
              tags: get("Tags") || "",
              flags: get("Flags") || "",
              priceType: get("Preisart") || "",
              price: priceValue,
              priceValueAddedTax: get("MwSt") || 0,
              amount: get("VerfügbareAnzahl") || 0,
              allowRecurringBooking: get("WiederkehrendeBuchungen") || "Nein",
              autoCommitBooking: get("AutomatischeFreigabe") || "Nein",
              isBookable: get("Istbuchbar") || "Nein",
              isPublic: get("Istöffentlich") || "Nein",
              imageName,
              imageUrl: this.zipImages
                ? this.zipImages.find((img) => img.name === imageName)?.blobUrl
                : null,
            };
            item.errors = this.validateBookables(item);
            return item;
          });
        } else if (this.type === "events") {
          this.tableRows = newRows.map((row, idx) => {
            const get = (label) => this.getRowValue(row, label);

            const item = {
              id: idx + 1,
              name: get("Name") || "",
              teaserText: get("Kurzbeschreibung") || "",
              description: get("Beschreibung") || "",
              tags: get("Tags") || "",
              flags: get("Flags") || "",
              startDate: get("Startdatum") || "",
              startTime: get("Startuhrzeit") || "",
              endDate: get("Enddatum") || "",
              endTime: get("Enduhrzeit") || "",
              locationName: get("NameVeranstaltungsort") || "",
              street: get("Straße") || "",
              houseNumber: get("Hausnummer") || "",
              addressAdditional: get("Adresszusatz") || "",
              zip: get("Postleitzahl") || "",
              city: get("Stadt") || "",
              organizerName: get("VeranstaltersName") || "",
              contactName: get("KontaktpersonName") || "",
              contactPhone: get("KontaktpersonTelefon") || "",
              contactEmail: get("KontaktpersonEmail") || "",
              needsRegistration: get("RegistrierungErforderlich") || "Nein",
              maxAttendees: get("Max.Teilnehmer") || "",
              isPublic: get("IstSichtbar") || "Nein",
              imageName: get("Titelbild") || "",
              imageUrl: this.zipImages?.find((x) => x.name === get("Titelbild"))
                ?.blobUrl,
            };

            item.errors = this.validateEvents(item);
            return item;
          });
        }
      },
    },
    tableRows(newValue) {
      if (newValue.length === 0) {
        this.$emit("back");
      }
    },
  },
  methods: {
    ...mapActions({
      addToast: "toasts/add",
    }),
    getRowValue(row, label) {
      const normalizedLabel = label.replace(/\s+/g, "").toLowerCase();
      const key = Object.keys(row).find(
        (k) => k.replace(/\s+/g, "").toLowerCase() === normalizedLabel
      );
      return row[key];
    },
    validateBookables(item) {
      const errors = {};
      const validTypes = ["ticket", "räume", "ressourcen"];

      if (!validTypes.includes(String(item.type).toLowerCase().trim())) {
        errors.type = "Typ muss sein: Ticket, Räume oder Ressourcen";
      }

      if (!item.title || String(item.title).trim() === "") {
        errors.title = "Titel ist erforderlich";
      }

      if (
        !["pro Stück", "pro Stunde", "pro Tag", "pro Quadratmeter"].includes(
          item.priceType
        )
      ) {
        errors.priceType =
          "Der Preis muss angegeben werden: pro Stück, pro Stunde, pro Tag oder pro Quadratmeter";
      }

      if (isNaN(Number(item.price)))
        errors.price = "Der Preis muss eine Zahl sein";
      if (isNaN(Number(item.priceValueAddedTax)))
        errors.priceValueAddedTax = "MWSt muss eine Zahl sein";
      if (isNaN(Number(item.amount)))
        errors.amount = "Der Betrag muss eine Zahl sein";

      const validBoolValues = ["ja", "nein"];
      const checkBool = (val) =>
        validBoolValues.includes(String(val).toLowerCase().trim());

      if (!checkBool(item.allowRecurringBooking))
        errors.allowRecurringBooking = "Der Wert muss Ja oder Nein sein";
      if (!checkBool(item.autoCommitBooking))
        errors.autoCommitBooking = "Der Wert muss Ja oder Nein sein";
      if (!checkBool(item.isBookable))
        errors.isBookable = "Der Wert muss Ja oder Nein sein";
      if (!checkBool(item.isPublic))
        errors.isPublic = "Der Wert muss Ja oder Nein sein";

      return errors;
    },
    validateEvents(item) {
      const errors = {};
      if (!item.name?.trim()) errors.name = "Name ist erforderlich";

      const startDate = this.parseDate(item.startDate);
      const endDate = this.parseDate(item.endDate);
      if (!startDate)
        errors.startDate =
          "Datum muss im Format TT-MM-JJJJ sein (z. B. 25-12-2025)";
      if (!endDate)
        errors.endDate =
          "Datum muss im Format TT-MM-JJJJ sein (z. B. 25-12-2025)";

      const isValidTime = (value) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);
      if (item.startTime && !isValidTime(item.startTime))
        errors.startTime = "Uhrzeit muss im Format HH:MM sein (z. B. 09:30)";
      if (item.endTime && !isValidTime(item.endTime))
        errors.endTime = "Uhrzeit muss im Format HH:MM sein (z. B. 17:45)";

      if (item.contactEmail && item.contactEmail.trim() !== "") {
        const pattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
        if (!pattern.test(item.contactEmail.trim())) {
          errors.contactEmail = "Ungültige Email-Adresse";
        }
      }

      const validBoolValues = ["ja", "nein"];
      const checkBool = (val) =>
        validBoolValues.includes(String(val).toLowerCase().trim());
      if (!checkBool(item.needsRegistration))
        errors.needsRegistration = "Der Wert muss Ja oder Nein sein";
      if (!checkBool(item.isPublic))
        errors.isPublic = "Der Wert muss Ja oder Nein sein";

      if (item.maxAttendees && isNaN(Number(item.maxAttendees)))
        errors.maxAttendees = "Muss eine Zahl sein";

      return errors;
    },
    deleteRow(item) {
      this.tableRows = this.tableRows.filter((i) => i.id !== item.id);
      this.selected = this.selected.filter((i) => i.id !== item.id);
    },
    deleteSelected() {
      this.tableRows = this.tableRows.filter((i) => !this.selected.includes(i));
      this.selected = [];
    },
    mapToBookablePayload(item) {
      const normalize = (val) =>
        (val || "").toString().trim().toLowerCase().replace(/\s+/g, " ");

      const mappedType = this.bookableTypeMap[normalize(item.type)];
      const mappedPriceType = this.priceTypeMap[normalize(item.priceType)];
      return {
        tenantId: this.tenantId,
        type: mappedType,
        title: item.title,
        description: item.description,
        imgUrl: item.imgUrl,
        flags: item.flags ? item.flags.split(",").map((f) => f.trim()) : [],
        tags: item.tags ? item.tags.split(",").map((t) => t.trim()) : [],
        location: item.location,
        eventId: item.eventId || undefined,
        priceCategories: [
          {
            priceEur: item.price,
            fixedPrice: false,
            interval: {
              start: null,
              end: null,
            },
          },
        ],
        priceType: mappedPriceType,
        priceValueAddedTax: item.priceValueAddedTax || 0,
        amount: Number(item.amount) || 0,
        allowRecurringBooking:
          item.allowRecurringBooking?.trim()?.toLowerCase() === "ja"
            ? true
            : false,
        autoCommitBooking:
          item.autoCommitBooking?.trim()?.toLowerCase() === "ja" ? true : false,
        isBookable:
          item.isBookable?.trim()?.toLowerCase() === "ja" ? true : false,
        isPublic: item.isPublic?.trim()?.toLowerCase() === "ja" ? true : false,
      };
    },
    mapToEventPayload(item) {
      return {
        tenantId: this.tenantId,
        information: {
          name: item.name,
          teaserText: item.teaserText,
          description: item.description,
          teaserImage: item.imgUrl ? item.imgUrl : "",
          startDate: this.formatDate(item.startDate),
          startTime: item.startTime,
          endDate: this.formatDate(item.endDate),
          endTime: item.endTime,
          tags: item.tags ? item.tags.split(",").map((t) => t.trim()) : [],
          flags: item.flags ? item.flags.split(",").map((f) => f.trim()) : [],
        },
        eventLocation: {
          name: item.locationName,
        },
        eventAddress: {
          street: item.street,
          houseNumber: item.houseNumber,
          additional: item.addressAdditional,
          city: item.city,
          zip: item.zip,
        },
        eventOrganizer: {
          name: item.organizerName,
          contactPersonName: item.contactName,
          contactPersonPhoneNumber: item.contactPhone,
          contactPersonEmailAddress: item.contactEmail,
        },
        attendees: {
          needsRegistration:
            item.needsRegistration?.trim()?.toLowerCase() === "ja"
              ? true
              : false,
          maxAttendees: Number(item.maxAttendees) || null,
        },
        isPublic: item.isPublic?.trim()?.toLowerCase() === "ja" ? true : false,
      };
    },
    async importSingleItem(item) {
      if (item.errors && Object.keys(item.errors).length) {
        this.addToast(
          ToastService.createToast(
            this.type === "bookables"
              ? "bookablesBulkImport.hasErrors"
              : "eventsBulkImport.hasErrors",
            "error",
            2000
          )
        );
        return;
      }
      this.uploadingItemId = item.id;
      await this.importSelectedItems([item]);
      this.uploadingItemId = null;
    },
    async importSelectedItems(items) {
      try {
        this.isLoading = true;
        const imagesToUpload = items
          .map((row) => {
            const match = this.zipImages.find(
              (img) => img.name === row.imageName
            );
            if (!match) return null;
            return {
              fileName: row.imageName,
              blob: match.file,
              mimeType: match?.mime,
              row,
            };
          })
          .filter(Boolean);

        if (imagesToUpload?.length > 0) {
          const formData = new FormData();
          formData.append("accessLevel", "public");
          formData.append(
            "customDirectory",
            this.type === "bookables" ? "bulk-bookables" : "bulk-events"
          );
          imagesToUpload.forEach(({ fileName, blob, mimeType }) => {
            const file = new File([blob], fileName, {
              type: mimeType || "image/jpeg",
            });
            formData.append("file", file);
          });
          const uploadResponse = await ApiFileService.createFiles(
            this.tenantId,
            formData
          );
          const uploaded = uploadResponse?.data || [];
          items = items.map((item) => {
            const match = uploaded.find((u) => u.fileName === item.imageName);
            return match
              ? {
                  ...item,
                  imgUrl: match.link ? match.link : item.imgUrl,
                }
              : item;
          });
        }

        if (this.type === "bookables") {
          const payload = items.map((item) => this.mapToBookablePayload(item));
          await ApiBookablesService.createBookables(this.tenantId, payload);
        } else {
          const payload = items.map((item) => this.mapToEventPayload(item));
          await ApiEventService.createEvents(this.tenantId, payload);
        }
        this.tableRows = this.tableRows.filter(
          (i) => !items.some((s) => s.id === i.id)
        );
        Matomo.trackEvent([
          "trackEvent",
          `${this.type === "bookables" ? "Bookable" : "Event"}`,
          "Create",
          `Bulk ${items?.length}`,
        ]);
        items = [];
        this.selected = [];
        this.addToast(
          ToastService.createToast(
            this.type === "bookables"
              ? "bookablesBulkImport.success"
              : "eventsBulkImport.success",
            "success",
            2000
          )
        );
        this.dialog = false;
        this.$emit("confirm-import");
      } catch (error) {
        console.log(error);
        if (this.type === "bookables") {
          this.addToast(
            ToastService.createToast("bookablesBulkImport.error", "error", 2000)
          );
        } else {
          this.addToast(
            ToastService.createToast("eventsBulkImport.error", "error", 2000)
          );
        }
      } finally {
        this.isLoading = false;
      }
    },
    parseDate(dateString) {
      if (typeof dateString !== "string") return null;
      const regex = /^\d{2}-\d{2}-\d{4}$/;
      if (!regex.test(dateString)) return null;
      const parsed = parse(dateString, "dd-MM-yyyy", new Date());
      return isValid(parsed) ? parsed : null;
    },
    formatDate(dateStr) {
      if (!dateStr) return null;
      const [day, month, year] = dateStr.split("-");
      if (!day || !month || !year) return null;
      return `${year}-${month}-${day}`;
    },
  },
  mounted() {
    const display = useDisplay();

    this.mdAndUp = display.mdAndUp.value;
    this.smAndDown = display.smAndDown.value;
  },
};
</script>

<style scoped>
.v-theme--light.v-icon {
  color: rgba(0, 0, 0, 0.54);
}
</style>
